(() => {
  'use strict';

  if (window.HalaDeliveryMapLoaded) return;
  window.HalaDeliveryMapLoaded = true;

  const CARD_SELECTOR = '[data-hala-delivery-card]';
  const BAHRAIN_CENTER = { lat: 26.0667, lng: 50.5577 };
  const DEFAULT_ZOOM = 12;
  const SELECTED_ZOOM = 18;
  const GEOLOCATION_TIMEOUT = 12000;

  let googleMapsPromise = null;
  let modal = null;
  let map = null;
  let userMarker = null;
  let activeCard = null;
  let returnFocus = null;
  let selectedPosition = null;
  let checkoutBusy = false;
  let saveNoteTimer = null;

  function getCard() {
    if (activeCard && activeCard.isConnected) return activeCard;
    activeCard = document.querySelector(`#cart-drawer ${CARD_SELECTOR}`) ||
      document.querySelector(CARD_SELECTOR);
    return activeCard;
  }

  function parseCoordinates(value) {
    if (!value) return null;

    const parts = String(value)
      .split(',')
      .map((part) => Number(part.trim()));

    if (
      parts.length !== 2 ||
      parts.some((part) => !Number.isFinite(part)) ||
      Math.abs(parts[0]) > 90 ||
      Math.abs(parts[1]) > 180
    ) {
      return null;
    }

    return { lat: parts[0], lng: parts[1] };
  }

  function formatCoordinates(position) {
    return `${Number(position.lat).toFixed(7)},${Number(position.lng).toFixed(7)}`;
  }

  function mapsLink(position) {
    const coordinates = formatCoordinates(position);
    return `https://www.google.com/maps?q=${coordinates}`;
  }

  function setCardError(card, message) {
    if (!card) return;
    const box = card.querySelector('[data-hala-card-error]');
    if (!box) return;

    box.textContent = message || '';
    box.hidden = !message;
  }

  function setMapError(message) {
    if (!modal) return;
    const box = modal.querySelector('[data-hala-map-error]');
    if (!box) return;

    box.textContent = message || '';
    box.hidden = !message;
  }

  function setLoading(isLoading, title, copy) {
    if (!modal) return;

    const overlay = modal.querySelector('[data-hala-map-loading]');
    const titleElement = modal.querySelector('[data-hala-map-loading-title]');
    const copyElement = modal.querySelector('[data-hala-map-loading-copy]');

    if (title && titleElement) titleElement.textContent = title;
    if (copy && copyElement) copyElement.textContent = copy;

    overlay?.classList.toggle('is-hidden', !isLoading);
  }

  function setMapStatus(title, copy) {
    if (!modal) return;

    const titleElement = modal.querySelector('[data-hala-map-status]');
    const copyElement = modal.querySelector('[data-hala-map-coordinates]');

    if (titleElement && title) titleElement.textContent = title;
    if (copyElement && copy) copyElement.textContent = copy;
  }

  function setConfirmEnabled(enabled) {
    const button = modal?.querySelector('[data-hala-confirm-map]');
    if (button) button.disabled = !enabled;
  }

  function waitForExistingGoogleScript(existingScript, resolve, reject) {
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;

      if (window.google?.maps?.Map) {
        window.clearInterval(timer);
        resolve(window.google.maps);
        return;
      }

      if (attempts >= 160) {
        window.clearInterval(timer);
        reject(new Error('Google Maps took too long to load.'));
      }
    }, 100);

    existingScript.addEventListener(
      'error',
      () => {
        window.clearInterval(timer);
        reject(new Error('Google Maps failed to load.'));
      },
      { once: true }
    );
  }

  function loadGoogleMaps(apiKey) {
    if (window.google?.maps?.Map) {
      return Promise.resolve(window.google.maps);
    }

    if (googleMapsPromise) return googleMapsPromise;

    googleMapsPromise = new Promise((resolve, reject) => {
      if (!apiKey) {
        reject(new Error('The Google Maps API key is missing.'));
        return;
      }

      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com/maps/api/js"]'
      );

      if (existingScript) {
        waitForExistingGoogleScript(existingScript, resolve, reject);
        return;
      }

      const callbackName = '__halaGoogleMapsReady';
      let timeout;

      window[callbackName] = () => {
        window.clearTimeout(timeout);
        delete window[callbackName];

        if (window.google?.maps?.Map) {
          resolve(window.google.maps);
        } else {
          reject(new Error('Google Maps loaded without the required library.'));
        }
      };

      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: apiKey,
        callback: callbackName,
        loading: 'async',
        v: 'weekly',
        region: 'BH'
      });

      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.dataset.halaGoogleMaps = 'true';
      script.onerror = () => {
        window.clearTimeout(timeout);
        delete window[callbackName];
        googleMapsPromise = null;
        reject(new Error('Google Maps failed to load. Check the API key and billing.'));
      };

      timeout = window.setTimeout(() => {
        googleMapsPromise = null;
        reject(new Error('Google Maps took too long to load.'));
      }, 18000);

      document.head.appendChild(script);
    });

    return googleMapsPromise;
  }

  function createModal(card) {
    if (modal?.isConnected) return modal;

    const template = card?.querySelector('[data-hala-map-template]');
    if (!template) throw new Error('The delivery map template is missing.');

    const fragment = template.content.cloneNode(true);
    modal = fragment.querySelector('[data-hala-map-modal]');
    document.body.appendChild(modal);
    bindModalEvents();

    return modal;
  }

  function showModalDialog() {
    if (!modal) return;

    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }

    document.documentElement.classList.add('hala-map-is-open');
  }

  function closeModal() {
    if (!modal) return;

    if (typeof modal.close === 'function' && modal.open) {
      modal.close();
    } else {
      modal.removeAttribute('open');
      handleModalClosed();
    }
  }

  function handleModalClosed() {
    document.documentElement.classList.remove('hala-map-is-open');
    setMapError('');

    if (returnFocus && typeof returnFocus.focus === 'function') {
      window.setTimeout(() => returnFocus.focus(), 0);
    }
  }

  function bindModalEvents() {
    if (!modal || modal.dataset.halaBound === 'true') return;
    modal.dataset.halaBound = 'true';

    modal.querySelector('[data-hala-close-map]')?.addEventListener('click', closeModal);
    modal.querySelector('[data-hala-locate-me]')?.addEventListener('click', () => {
      requestCurrentLocation({ userInitiated: true });
    });
    modal.querySelector('[data-hala-confirm-map]')?.addEventListener('click', confirmSelection);

    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModal();
    });

    modal.addEventListener('close', handleModalClosed);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
  }

  function createUserMarker(position) {
    if (!map || !window.google?.maps) return;

    if (!userMarker) {
      userMarker = new window.google.maps.Marker({
        map,
        clickable: false,
        optimized: true,
        zIndex: 2,
        title: 'Your current location',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeOpacity: 1,
          strokeWeight: 3
        }
      });
    }

    userMarker.setPosition(position);
    userMarker.setMap(map);
  }

  function updateSelectionFromMap() {
    if (!map) return;

    const center = map.getCenter();
    if (!center) return;

    selectedPosition = {
      lat: center.lat(),
      lng: center.lng()
    };

    setMapStatus('Pin placed at this location', formatCoordinates(selectedPosition));
    setConfirmEnabled(true);
  }

  function initialiseMap(initialPosition) {
    const mapElement = modal?.querySelector('[data-hala-google-map]');
    if (!mapElement) throw new Error('The Google Map container is missing.');

    if (!map) {
      map = new window.google.maps.Map(mapElement, {
        center: initialPosition || BAHRAIN_CENTER,
        zoom: initialPosition ? SELECTED_ZOOM : DEFAULT_ZOOM,
        backgroundColor: '#eae5e1',
        clickableIcons: false,
        disableDefaultUI: true,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        keyboardShortcuts: true,
        mapTypeControl: false,
        streetViewControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_TOP
        }
      });

      const stage = modal.querySelector('.hala-map-stage');

      map.addListener('dragstart', () => {
        stage?.classList.add('is-moving');
        setMapStatus('Move the map beneath the pin', 'Release when the pin is on the exact entrance');
      });

      map.addListener('dragend', () => {
        stage?.classList.remove('is-moving');
      });

      map.addListener('idle', updateSelectionFromMap);
    } else {
      map.setCenter(initialPosition || BAHRAIN_CENTER);
      map.setZoom(initialPosition ? SELECTED_ZOOM : DEFAULT_ZOOM);
      window.google.maps.event.trigger(map, 'resize');
    }

    setLoading(false);
    updateSelectionFromMap();
  }

  function geolocationMessage(error) {
    if (!error) return 'We could not detect your current location.';

    switch (error.code) {
      case 1:
        return 'Location permission was not allowed. You can still drag the map and choose the delivery point manually.';
      case 2:
        return 'Your current location is unavailable. You can still choose the delivery point manually.';
      case 3:
        return 'Finding your current location took too long. You can still choose the delivery point manually.';
      default:
        return 'We could not detect your current location. You can still choose it manually.';
    }
  }

  function requestCurrentLocation({ userInitiated = false } = {}) {
    if (!map || !modal) return;

    const locateButton = modal.querySelector('[data-hala-locate-me]');
    locateButton?.classList.add('is-locating');
    setMapError('');
    setMapStatus('Finding your current location…', 'Please allow location access when your browser asks');

    if (!navigator.geolocation) {
      locateButton?.classList.remove('is-locating');
      setMapError('This browser does not support location detection. Drag the map to choose your delivery point.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        locateButton?.classList.remove('is-locating');

        const currentPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        createUserMarker(currentPosition);
        map.panTo(currentPosition);
        map.setZoom(SELECTED_ZOOM);
        selectedPosition = currentPosition;
        setMapStatus('Your current location is centred', 'Drag the map if the delivery entrance is somewhere else');
        setConfirmEnabled(true);
      },
      (error) => {
        locateButton?.classList.remove('is-locating');
        setMapError(geolocationMessage(error));
        setMapStatus('Choose the location manually', 'Drag the map beneath the fixed pin');

        if (userInitiated) {
          map.panTo(selectedPosition || BAHRAIN_CENTER);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: GEOLOCATION_TIMEOUT,
        maximumAge: 300000
      }
    );
  }

  async function openPicker(card) {
    if (!card) return;

    activeCard = card;
    returnFocus = document.activeElement;
    setCardError(card, '');

    try {
      createModal(card);
      showModalDialog();
      setLoading(true, 'Loading Google Maps…', 'Please wait a moment');
      setConfirmEnabled(false);
      setMapError('');

      const savedPosition = parseCoordinates(
        card.querySelector('[data-hala-location-coordinates]')?.value
      );

      selectedPosition = savedPosition || BAHRAIN_CENTER;

      await loadGoogleMaps(card.dataset.halaGoogleMapsKey);

      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          try {
            initialiseMap(savedPosition);

            if (savedPosition) {
              setMapStatus('Saved delivery location', 'Drag the map to change the exact point');
            } else {
              requestCurrentLocation();
            }
          } catch (error) {
            setLoading(false);
            setMapError(error.message || 'The map could not be started.');
          }
        }, 50);
      });
    } catch (error) {
      setLoading(false);
      setMapError(error.message || 'The map could not load. Please refresh and try again.');
    }
  }

  function updateCard(card, position) {
    if (!card || !position) return;

    const coordinates = formatCoordinates(position);
    const coordinatesInput = card.querySelector('[data-hala-location-coordinates]');
    const linkInput = card.querySelector('[data-hala-location-link]');
    const status = card.querySelector('[data-hala-card-status]');
    const label = card.querySelector('[data-hala-open-map-label]');
    const check = card.querySelector('[data-hala-card-check]');

    if (coordinatesInput) coordinatesInput.value = coordinates;
    if (linkInput) linkInput.value = mapsLink(position);
    if (status && status.textContent.trim() !== 'Location added — ready for checkout') {
      status.textContent = 'Location added — ready for checkout';
    }
    if (label && label.textContent.trim() !== 'Change location') {
      label.textContent = 'Change location';
    }
    if (check) check.hidden = false;

    card.classList.add('is-complete');
    setCardError(card, '');
  }

  function getAttributes(card) {
    const attributes = {};

    card?.querySelectorAll('[name^="attributes["]').forEach((field) => {
      const match = field.name.match(/^attributes\[(.+)]$/);
      if (!match) return;
      attributes[match[1]] = String(field.value || '').trim();
    });

    return attributes;
  }

  async function saveAttributes(card) {
    if (!card) throw new Error('The delivery location form is unavailable.');

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: getAttributes(card) })
    });

    if (!response.ok) {
      throw new Error('Shopify could not save the delivery location.');
    }

    return response.json();
  }

  async function confirmSelection() {
    const card = getCard();
    const button = modal?.querySelector('[data-hala-confirm-map]');

    if (!card || !map || !button) return;

    updateSelectionFromMap();

    if (!selectedPosition) {
      setMapError('Move the map and place the pin on the delivery location first.');
      return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Saving location…';
    setMapError('');

    updateCard(card, selectedPosition);

    try {
      await saveAttributes(card);
      button.textContent = 'Location saved ✓';

      window.setTimeout(() => {
        closeModal();
        button.textContent = originalText;
        button.disabled = false;
      }, 450);
    } catch (error) {
      button.textContent = originalText;
      button.disabled = false;
      setMapError('We could not save the location. Please check your connection and try again.');
    }
  }

  function hasDeliveryLocation(card) {
    return Boolean(
      parseCoordinates(card?.querySelector('[data-hala-location-coordinates]')?.value)
    );
  }

  function findCheckoutControl(target) {
    const control = target?.closest?.(
      'a, button, input[type="submit"], input[type="button"]'
    );

    if (!control || !control.closest('#cart-drawer')) return null;

    const name = (control.getAttribute('name') || '').trim().toLowerCase();
    const href = (control.getAttribute('href') || '').trim().toLowerCase();
    const text = (control.textContent || control.value || '').trim().toLowerCase();

    const isCheckout =
      name === 'checkout' ||
      href.includes('/checkout') ||
      control.hasAttribute('data-checkout') ||
      text === 'checkout' ||
      text === 'check out' ||
      text.includes('proceed to checkout');

    return isCheckout ? control : null;
  }

  function setCheckoutBusy(control, busy) {
    if (!control) return;

    if (busy) {
      control.setAttribute('aria-disabled', 'true');

      if ('disabled' in control) control.disabled = true;

      if (control.tagName === 'INPUT') {
        control.dataset.halaOriginalValue = control.value || '';
        control.value = 'Saving delivery location…';
      } else {
        control.dataset.halaOriginalHtml = control.innerHTML;
        control.textContent = 'Saving delivery location…';
      }
    } else {
      control.removeAttribute('aria-disabled');
      if ('disabled' in control) control.disabled = false;

      if (control.tagName === 'INPUT') {
        if (control.dataset.halaOriginalValue !== undefined) {
          control.value = control.dataset.halaOriginalValue;
          delete control.dataset.halaOriginalValue;
        }
      } else if (control.dataset.halaOriginalHtml !== undefined) {
        control.innerHTML = control.dataset.halaOriginalHtml;
        delete control.dataset.halaOriginalHtml;
      }
    }
  }

  async function handleCheckout(event, control) {
    if (!control || checkoutBusy) return;

    const card = document.querySelector(`#cart-drawer ${CARD_SELECTOR}`);
    if (!card) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    if (!hasDeliveryLocation(card)) {
      setCardError(card, 'Please choose the exact delivery location before checkout.');
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      openPicker(card);
      return;
    }

    checkoutBusy = true;
    setCheckoutBusy(control, true);
    setCardError(card, '');

    try {
      await saveAttributes(card);
      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root}checkout`);
    } catch (error) {
      checkoutBusy = false;
      setCheckoutBusy(control, false);
      setCardError(card, 'We could not save your delivery details. Please try checkout again.');
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function hydrateCard(card) {
    if (!card) return;

    const position = parseCoordinates(
      card.querySelector('[data-hala-location-coordinates]')?.value
    );

    if (position) {
      updateCard(card, position);
    } else {
      card.classList.remove('is-complete');
      const check = card.querySelector('[data-hala-card-check]');
      if (check) check.hidden = true;
    }
  }

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest?.('[data-hala-open-map]');
    if (openButton) {
      event.preventDefault();
      openPicker(openButton.closest(CARD_SELECTOR));
      return;
    }

    const checkoutControl = findCheckoutControl(event.target);
    if (checkoutControl) {
      handleCheckout(event, checkoutControl);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const checkoutControl = findCheckoutControl(event.submitter);
    if (checkoutControl) {
      handleCheckout(event, checkoutControl);
    }
  }, true);

  document.addEventListener('input', (event) => {
    const note = event.target.closest?.('[data-hala-delivery-note]');
    if (!note) return;

    window.clearTimeout(saveNoteTimer);
    saveNoteTimer = window.setTimeout(() => {
      const card = note.closest(CARD_SELECTOR);
      if (card && hasDeliveryLocation(card)) {
        saveAttributes(card).catch(() => {
          // Checkout will retry saving. Keep typing interruption-free.
        });
      }
    }, 700);
  });

  const hydrateAll = () => {
    document.querySelectorAll(CARD_SELECTOR).forEach(hydrateCard);
  };

  hydrateAll();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(CARD_SELECTOR)) hydrateCard(node);
        node.querySelectorAll?.(CARD_SELECTOR).forEach(hydrateCard);
      });
    });
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
